// clientLogService.js — cattura un buffer ring dei log console/errori recenti del
// browser, utile per il debug quando un utente apre una segnalazione.
// Riutilizzabile su altri CRM Liberotratto: nessuna dipendenza esterna, nessun side
// effect finché non si chiama initClientLogCapture().

const MAX_EVENTS = 100; // ring buffer: teniamo solo gli ultimi N eventi
const MAX_MSG_CHARS = 2000; // tronca i messaggi molto lunghi
const MAX_SNAPSHOT_BYTES = 50 * 1024; // limite payload finale (~50KB)

const buffer = [];
let installed = false;
// anti-loop: true SOLO mentre push() sta scrivendo nel buffer, per evitare doppie
// catture/ricorsione. Fuori da push() resta false, così la cattura è sempre attiva.
let capturing = false;

// Serializzazione sicura: gestisce circular refs, Error, e oggetti grandi.
function safeStringify(value) {
  if (typeof value === "string") return value;
  if (value instanceof Error) {
    return value.stack || `${value.name}: ${value.message}`;
  }
  const seen = new WeakSet();
  try {
    const out = JSON.stringify(value, (_k, v) => {
      if (v instanceof Error) return v.stack || `${v.name}: ${v.message}`;
      if (typeof v === "bigint") return v.toString();
      if (typeof v === "function") return `[Function ${v.name || "anonymous"}]`;
      if (typeof v === "object" && v !== null) {
        if (seen.has(v)) return "[Circular]";
        seen.add(v);
      }
      return v;
    });
    return out === undefined ? String(value) : out;
  } catch {
    try {
      return String(value);
    } catch {
      return "[Unserializable]";
    }
  }
}

function serializeArgs(args) {
  return args
    .map((a) => safeStringify(a))
    .join(" ")
    .slice(0, MAX_MSG_CHARS);
}

function push(level, message, stack) {
  if (capturing) return; // già dentro una cattura → evita loop/doppie catture
  capturing = true;
  try {
    buffer.push({
      level,
      timestamp: new Date().toISOString(),
      message,
      url: typeof window !== "undefined" ? window.location.href : "",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      ...(stack ? { stack: String(stack).slice(0, MAX_MSG_CHARS) } : {}),
    });
    if (buffer.length > MAX_EVENTS)
      buffer.splice(0, buffer.length - MAX_EVENTS);
  } finally {
    capturing = false;
  }
}

// Avvolge un metodo console preservando il comportamento originale.
function wrapConsole(console, name) {
  const original = console[name];
  if (typeof original !== "function") return;
  console[name] = function (...args) {
    try {
      push(name, serializeArgs(args));
    } catch {
      // non rompere mai la console
    }
    return original.apply(this, args);
  };
}

// API pubblica ────────────────────────────────────────────────────────────────

// Installa la cattura una sola volta. Idempotente.
export function initClientLogCapture() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Cattura anche console.log: nei test l'utente usa naturalmente console.log("...").
  wrapConsole(window.console, "log");
  wrapConsole(window.console, "error");
  wrapConsole(window.console, "warn");
  wrapConsole(window.console, "info");

  window.addEventListener("error", (e) => {
    const msg = e?.message || "window.onerror";
    const where = e?.filename ? ` (${e.filename}:${e.lineno}:${e.colno})` : "";
    push("error", `${msg}${where}`, e?.error?.stack);
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e?.reason;
    push(
      "error",
      `Unhandled promise rejection: ${safeStringify(reason).slice(
        0,
        MAX_MSG_CHARS
      )}`,
      reason?.stack
    );
  });
}

// Restituisce uno snapshot (copia) dei log, già limitato a ~50KB.
export function getClientLogsSnapshot() {
  let snapshot = buffer.slice();
  // Riduci finché il payload serializzato rientra nel limite.
  while (
    snapshot.length &&
    JSON.stringify(snapshot).length > MAX_SNAPSHOT_BYTES
  ) {
    snapshot = snapshot.slice(Math.ceil(snapshot.length / 4)); // scarta i più vecchi a blocchi
  }
  return snapshot;
}

// Svuota il buffer (opzionale).
export function clearClientLogs() {
  buffer.length = 0;
}

export default { initClientLogCapture, getClientLogsSnapshot, clearClientLogs };
